/**
 * IndiaRoutes — India Regulatory Hub, India Risk Pulse (public + admin
 * widget), India alerts, cross-regulator tools, /india/:slug deep pages.
 *
 * Pulled out of App.js for lazy code-splitting — the India Hub is a large
 * sub-product (6 regulators × 4 layers + 5 cross-tools + 12 templates), so
 * isolating its chunk saves bytes on initial paint for non-Indian visitors.
 */
import { lazy } from "react";
import { Route } from "react-router-dom";

const India = lazy(() => import("@/pages/India"));
const IndiaHub = lazy(() => import("@/pages/india/IndiaHub"));
const IndiaSlugDispatcher = lazy(() => import("@/pages/india/IndiaSlugDispatcher"));
const IndiaLayer = lazy(() => import("@/pages/india/IndiaLayer"));
const IndiaCross = lazy(() => import("@/pages/india/IndiaCross"));
const IndiaTemplates = lazy(() => import("@/pages/india/IndiaTemplates"));
const IndiaHubGlossary = lazy(() => import("@/pages/india/IndiaHubGlossary"));
const IndiaAlerts = lazy(() => import("@/pages/india/IndiaAlerts"));
const RbiLatestCircular = lazy(() => import("@/pages/india/RbiLatestCircular"));

// RBI Model Risk 2026 (Draft Guidance) — P1 knowledge pages
const ModelRisk = lazy(() => import("@/pages/india/mrm/ModelRisk"));
const ModelRiskAi = lazy(() => import("@/pages/india/mrm/ModelRiskAi"));
const ModelRiskThirdParty = lazy(() => import("@/pages/india/mrm/ModelRiskThirdParty"));

// India Risk Pulse family
const IndiaRiskPulse = lazy(() => import("@/pages/IndiaRiskPulse"));
const IndiaRiskPulseArchive = lazy(() => import("@/pages/IndiaRiskPulseArchive"));
const IndiaRiskPulseEmbed = lazy(() => import("@/pages/IndiaRiskPulseEmbed"));
const IndiaRiskPulseApi = lazy(() => import("@/pages/IndiaRiskPulseApi"));
const IndiaRiskPulseApiUpgrade = lazy(() => import("@/pages/IndiaRiskPulseApiUpgrade"));
const IndiaRiskPulseApiStatus = lazy(() => import("@/pages/IndiaRiskPulseApiStatus"));
const RiskSignalsWidget = lazy(() => import("@/pages/widgets/RiskSignalsWidget"));

export const indiaRoutes = [
  // Hub root + tracker + templates + glossary
  <Route key="india" path="/india" element={<IndiaHub />} />,
  <Route key="india-tracker" path="/india/tracker" element={<India />} />,
  <Route key="india-templates" path="/india/templates" element={<IndiaTemplates />} />,
  <Route key="india-glossary" path="/india/glossary" element={<IndiaHubGlossary />} />,
  <Route key="india-alerts" path="/india/alerts" element={<IndiaAlerts />} />,

  // Cross-regulator tools (explicit slugs first so they win over /india/:slug)
  <Route key="india-breach" path="/india/breach-playbook" element={<IndiaCross slug="breach-playbook" />} />,
  <Route key="india-overlap" path="/india/overlap-matrix" element={<IndiaCross slug="overlap-matrix" />} />,
  <Route key="india-entity-nav" path="/india/entity-navigator" element={<IndiaCross slug="entity-navigator" />} />,
  <Route key="india-ai-bfsi" path="/india/ai-bfsi" element={<IndiaCross slug="ai-bfsi" />} />,
  <Route key="india-board-gov" path="/india/board-governance" element={<IndiaCross slug="board-governance" />} />,
  <Route key="india-rbi-circular" path="/india/rbi/latest-circular" element={<RbiLatestCircular />} />,
  // RBI Model Risk 2026 (Draft) — declared BEFORE /india/:slug so they win.
  <Route key="india-rbi-mrm" path="/india/rbi/model-risk" element={<ModelRisk />} />,
  <Route key="india-rbi-mrm-ai" path="/india/rbi/model-risk/ai" element={<ModelRiskAi />} />,
  <Route key="india-rbi-mrm-tp" path="/india/rbi/model-risk/third-party" element={<ModelRiskThirdParty />} />,

  // India Risk Pulse
  <Route key="irp" path="/india-risk-pulse" element={<IndiaRiskPulse />} />,
  <Route key="irp-archive" path="/india-risk-pulse/archive" element={<IndiaRiskPulseArchive />} />,
  <Route key="irp-embed" path="/india-risk-pulse/embed" element={<IndiaRiskPulseEmbed />} />,
  <Route key="irp-api" path="/india-risk-pulse/api" element={<IndiaRiskPulseApi />} />,
  <Route key="irp-api-upgrade" path="/india-risk-pulse/api/upgrade" element={<IndiaRiskPulseApiUpgrade />} />,
  <Route key="irp-api-status" path="/india-risk-pulse/api/status" element={<IndiaRiskPulseApiStatus />} />,
  <Route key="irp-tag" path="/india-risk-pulse/:tag" element={<IndiaRiskPulse />} />,
  <Route key="risk-signals-widget" path="/widgets/risk-signals" element={<RiskSignalsWidget />} />,

  // Generic regulator pages (must come last so explicit cross-tool slugs match first)
  <Route key="india-slug" path="/india/:slug" element={<IndiaSlugDispatcher />} />,
  <Route key="india-slug-layer" path="/india/:slug/:layer" element={<IndiaLayer />} />,
];
